import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Save, ArrowLeft, Loader2, ImageIcon, Link2, FileText, Award } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { RECONOCIMIENTOS } from "@/data/content";

const FIELDS = [
  { key: "logo_url", label: "Logo del evento", icon: ImageIcon, hint: "URL de la imagen del logo (se muestra en el hero y el encabezado)." },
  { key: "hero_image_url", label: "Fotografía de la presidenta (recorte)", icon: ImageIcon, hint: "URL del recorte sobre el panorama. Si está vacío se usa la fotografía oficial integrada." },
  { key: "hero_panorama_url", label: "Panorama del malecón (fondo del hero)", icon: ImageIcon, hint: "URL de la foto de fondo del hero. Si está vacío se usa la panorámica generada del malecón de Tampico." },
  { key: "hero_texture_url", label: "Textura de fondo (glifos)", icon: ImageIcon, hint: "URL de la textura que acompaña al hero y al placeholder." },
  { key: "informe_pdf_url", label: "PDF del informe completo", icon: FileText, hint: "Enlace del documento oficial del Segundo Informe." },
  { key: "google_form_url", label: "URL del Google Form (embed)", icon: Link2, hint: "URL con ?embedded=true para el iframe de propuestas." },
];

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [pdfLines, setPdfLines] = useState("");

  const loadConfig = () => {
    axios.get(`${API}/site-config`).then((r) => {
      setConfig(r.data);
      setPdfLines(
        Object.entries(r.data.factcheck_pdfs || {})
          .map(([k, v]) => `${k}=${v}`)
          .join("\n")
      );
    });
  };

  useEffect(() => {
    if (token) loadConfig();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", data.token);
      setToken(data.token);
      toast.success("Sesión iniciada");
    } catch {
      toast.error("Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const factcheck_pdfs = {};
      pdfLines.split("\n").forEach((line) => {
        const idx = line.indexOf("=");
        if (idx > 0) {
          const k = line.slice(0, idx).trim();
          const v = line.slice(idx + 1).trim();
          if (k && v) factcheck_pdfs[k] = v;
        }
      });
      await axios.put(
        `${API}/site-config`,
        { ...config, factcheck_pdfs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuración guardada", { description: "Los cambios ya son visibles en el portal." });
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken("");
        toast.error("La sesión expiró, vuelve a entrar");
      } else {
        toast.error("No se pudo guardar la configuración");
      }
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const setAwardLink = (id, value) =>
    setConfig((c) => ({ ...c, award_links: { ...(c.award_links || {}), [id]: value } }));

  if (!token) {
    return (
      <div className="min-h-screen bg-arena-sand grain relative flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm bg-white rounded-3xl border border-border shadow-xl p-8"
        >
          <span className="inline-flex p-3 rounded-2xl bg-guinda/10 text-guinda">
            <Lock className="w-6 h-6" aria-hidden="true" />
          </span>
          <h1 className="font-serif text-2xl text-gris-charcoal mt-5">Panel de administración</h1>
          <p className="text-sm text-gris mt-2">Configura las imágenes y enlaces del portal.</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div>
              <label htmlFor="admin-password" className="block text-xs font-mono uppercase tracking-widest text-gris mb-2">
                Contraseña
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                autoComplete="current-password"
                className="w-full rounded-xl border border-input bg-arena-sand px-4 py-3 text-sm focus:outline-none focus:border-guinda transition-colors duration-200"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-guinda hover:bg-guinda-dark text-white py-3 text-sm font-medium transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              Entrar
            </button>
          </form>
          <Link to="/" data-testid="admin-back-link" className="mt-6 inline-flex items-center gap-2 text-sm text-gris hover:text-guinda transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver al portal
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arena-sand py-12 px-5">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-guinda">Administración</p>
            <h1 className="font-serif text-3xl text-gris-charcoal mt-2">Configuración del portal</h1>
          </div>
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-gris hover:text-guinda transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Ver portal
          </Link>
        </div>

        {!config ? (
          <p className="inline-flex items-center gap-3 text-gris">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Cargando configuración…
          </p>
        ) : (
          <div className="space-y-6">
            <section className="bg-white rounded-3xl border border-border p-6 sm:p-8" data-testid="admin-images-section">
              <h2 className="font-serif text-xl text-gris-charcoal mb-6 inline-flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-guinda" aria-hidden="true" /> Imágenes y enlaces principales
              </h2>
              <div className="space-y-5">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label htmlFor={`cfg-${f.key}`} className="block text-sm font-medium text-gris-charcoal">
                      {f.label}
                    </label>
                    <input
                      id={`cfg-${f.key}`}
                      type="url"
                      value={config[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={`admin-field-${f.key}`}
                      placeholder="https://…"
                      className="mt-1.5 w-full rounded-xl border border-input bg-arena-sand px-4 py-2.5 text-sm focus:outline-none focus:border-guinda transition-colors duration-200"
                    />
                    <p className="mt-1 text-xs text-gris/80">{f.hint}</p>
                  </div>
                ))}
                {config.logo_url && (
                  <div className="flex items-center gap-4 pt-2">
                    <img src={config.logo_url} alt="Vista previa del logo" className="h-16 w-16 object-contain rounded-xl bg-arena-light p-2" />
                    {config.hero_image_url && (
                      <img src={config.hero_image_url} alt="Vista previa de la fotografía" className="h-16 w-16 object-cover rounded-xl" />
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="bg-white rounded-3xl border border-border p-6 sm:p-8" data-testid="admin-awards-section">
              <h2 className="font-serif text-xl text-gris-charcoal mb-2 inline-flex items-center gap-2">
                <Award className="w-5 h-5 text-guinda" aria-hidden="true" /> Notas oficiales de reconocimientos
              </h2>
              <p className="text-xs text-gris/80 mb-6">Deja vacío para mostrar “disponible próximamente”.</p>
              <div className="space-y-4">
                {RECONOCIMIENTOS.map((r) => (
                  <div key={r.id}>
                    <label htmlFor={`award-${r.id}`} className="block text-sm text-gris-charcoal">
                      {r.nombre} <span className="text-gris/70 font-mono text-xs">· {r.anio}</span>
                    </label>
                    <input
                      id={`award-${r.id}`}
                      type="url"
                      value={config.award_links?.[r.id] || ""}
                      onChange={(e) => setAwardLink(r.id, e.target.value)}
                      data-testid={`admin-award-link-${r.id}`}
                      placeholder="https://…"
                      className="mt-1 w-full rounded-xl border border-input bg-arena-sand px-4 py-2 text-sm focus:outline-none focus:border-guinda transition-colors duration-200"
                    />
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-3xl border border-border p-6 sm:p-8" data-testid="admin-pdfs-section">
              <h2 className="font-serif text-xl text-gris-charcoal mb-2 inline-flex items-center gap-2">
                <FileText className="w-5 h-5 text-guinda" aria-hidden="true" /> Soportes PDF del fact-checking
              </h2>
              <p className="text-xs text-gris/80 mb-4">
                Uno por línea con el formato <code className="font-mono text-guinda">FT-01=https://…</code>
              </p>
              <textarea
                value={pdfLines}
                onChange={(e) => setPdfLines(e.target.value)}
                data-testid="admin-pdfs-textarea"
                rows={5}
                placeholder={"FT-01=https://…\nFT-02=https://…"}
                className="w-full rounded-xl border border-input bg-arena-sand px-4 py-3 text-sm font-mono focus:outline-none focus:border-guinda transition-colors duration-200"
              />
            </section>

            <div className="sticky bottom-5 flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-save-button"
                className="inline-flex items-center gap-2 rounded-full bg-guinda hover:bg-guinda-dark text-white px-8 py-3.5 text-sm font-medium shadow-xl transition-colors duration-200 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Save className="w-4 h-4" aria-hidden="true" />}
                Guardar cambios
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
